import React, { Component } from 'react';
import { Redirect, Link } from 'react-router-dom';
import { injectIntl, FormattedMessage } from 'react-intl';
import AjaxHandler from '../../commons/ajax/AjaxHandler.js';
import Security from '../../commons/security/Security.js';
import Loading from '../ui/Loading.js';
import crearEricCorpusService from './ericCorpusService.js';

/**
 * EricCorpusDocumentos — pantalla donde la empresa sube sus procedimientos
 * internos (SOPs) para que ERIC los use al responder, además de la
 * documentación de la plataforma.
 *
 * Permisos (gateados también en el backend; acá sólo se ocultan controles que
 * rebotarían en 403). Los tres se piden ADEMÁS de ERIC_CHATBOT_CONSULTAR, que es
 * la puerta de entrada a todos los endpoints de ERIC (la exige el monolito y la
 * vuelve a exigir el micro vec-fleet-eric):
 *   ERIC_CORPUS_LISTAR     ver la pantalla
 *   ERIC_CORPUS_CREAR      subir
 *   ERIC_CORPUS_MODIFICAR  pausar / reactivar
 *
 * Layout: sigue la convención de la app (`row > col-12 > card`, ver
 * OTAbm y 160+ pantallas más). La página es fluida como el resto; el único
 * `max-width` es el de los párrafos de texto corrido (~62ch), que es la regla
 * de legibilidad, no un tope de página. El estado vacío se centra con la grilla
 * (`col-lg-8 offset-lg-2`), no con píxeles.
 *
 * Visual: docs/design-system/nuevo-desarrollo-frontend.md §3 — Poppins, teal
 * #00c0bd, texto #6B6F82 (contraste 4.5:1 sobre blanco), hairline #e3ebf3,
 * sombra 0 2px 4px rgba(26,16,54,.08), radios 8–10px, motion 150–250ms.
 * Iconos FontAwesome (`fa fa-*`), los mismos que usa EricAnalyst.
 *
 * Dos estados con necesidades opuestas, renderizados distinto a propósito:
 *   - Sin documentos: primera impresión. Sin medidor (mostrar "0 de 60.000" es
 *     gritar una restricción que no existe todavía); la pantalla explica qué va
 *     a pasar con un ejemplo concreto.
 *   - Con documentos: gestión. Medidor visible, la lista manda, la carga es
 *     un botón en el header de la lista y toda la tarjeta acepta drag & drop.
 *
 * Mockup aprobado: https://claude.ai/code/artifact/d6df7941-9df4-4d40-9d84-7267a713561b
 */
export class EricCorpusDocumentos extends Component {
  /**
   * Caracteres por página de texto, para traducir el presupuesto a algo que un
   * humano pueda dimensionar. Es una orientación, no una medida: se muestra con
   * "unas" y el número exacto de tokens sigue viviendo en el backend.
   */
  static get CARACTERES_POR_PAGINA() {
    return 2400;
  }

  /**
   * Tope del archivo antes de subirlo. Espejo de `IngestaDeCorpus::MAX_BYTES`
   * del micro y de `EricCorpusCrearValidation::MAX_BYTES` del monolito.
   */
  static get MAX_MB() {
    return 40;
  }

  static get MAX_BYTES() {
    return EricCorpusDocumentos.MAX_MB * 1024 * 1024;
  }

  constructor(props) {
    super(props);

    this.ajaxHandler = new AjaxHandler();
    this._service = null;
    this._isMounted = false;
    this._fileInputRef = React.createRef();

    this.state = {
      redirectTo: null,
      cargando: true,
      documentos: [],
      presupuestoTotal: 60000,
      presupuestoEnUso: 0,
      // Nombre del archivo que se está subiendo, o null. Se muestra en la lista
      // como una fila fantasma para que la espera tenga lugar en la pantalla.
      subiendoArchivo: null,
      // Id del documento cuyo estado se está cambiando, o null.
      cambiandoEstadoId: null,
      errorSubida: null,
      exitoSubida: null,
      arrastrando: false,
    };

    this.handleElegirArchivo = this.handleElegirArchivo.bind(this);
    this.handleArchivoSeleccionado = this.handleArchivoSeleccionado.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleTeclaEnZona = this.handleTeclaEnZona.bind(this);
    this.handleCerrarAviso = this.handleCerrarAviso.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    if (!Security.hasPermission('ERIC_CHATBOT_CONSULTAR') || !Security.hasPermission('ERIC_CORPUS_LISTAR')) {
      this.setState({ redirectTo: '/error' });
      return;
    }
    this.ajaxHandler.subscribe(this);
    this._service = crearEricCorpusService(this.ajaxHandler);
    this._cargarDocumentos();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.ajaxHandler.unsubscribe();
  }

  _cargarDocumentos() {
    var self = this;
    this.setState({ cargando: true });
    this._service.listar().then(function (res) {
      if (!self._isMounted) return;
      self.setState({
        cargando: false,
        documentos: res.documentos,
        presupuestoTotal: res.presupuestoTotal,
        presupuestoEnUso: res.presupuestoEnUso,
      });
    });
  }

  // --- Subida -------------------------------------------------------------

  handleElegirArchivo(e) {
    if (e && e.stopPropagation) e.stopPropagation();
    if (this.state.subiendoArchivo) return;
    if (this._fileInputRef.current) {
      this._fileInputRef.current.click();
    }
  }

  handleTeclaEnZona(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleElegirArchivo();
    }
  }

  handleArchivoSeleccionado(e) {
    var archivo = e.target.files && e.target.files[0];
    e.target.value = ''; // permite reintentar el mismo archivo tras un rechazo
    if (archivo) {
      this._subir(archivo);
    }
  }

  handleDragOver(e) {
    e.preventDefault();
    if (!this.state.subiendoArchivo && !this.state.arrastrando) {
      this.setState({ arrastrando: true });
    }
  }

  handleDragLeave(e) {
    e.preventDefault();
    // Sólo apagar cuando el cursor sale de la zona, no al pasar entre hijos.
    if (e.currentTarget && e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    this.setState({ arrastrando: false });
  }

  handleDrop(e) {
    e.preventDefault();
    this.setState({ arrastrando: false });
    if (this.state.subiendoArchivo) return;
    var archivo = e.dataTransfer.files && e.dataTransfer.files[0];
    if (archivo) {
      this._subir(archivo);
    }
  }

  handleCerrarAviso() {
    this.setState({ errorSubida: null, exitoSubida: null });
  }

  _subir(archivo) {
    var self = this;

    // Corte de tamaño antes de tocar la red. El backend (monolito y micro) lo
    // vuelve a validar — esto no es la defensa, es no hacerle subir un archivo
    // enorme a alguien para rebotarlo después. El tope es el del micro
    // (IngestaDeCorpus::MAX_BYTES); si cambia allá, cambia MAX_MB acá y el
    // texto de la zona de carga sale solo.
    if (archivo.size > EricCorpusDocumentos.MAX_BYTES) {
      this.setState({
        subiendoArchivo: null,
        exitoSubida: null,
        errorSubida: {
          archivo: archivo.name,
          motivo: this._t('eric.corpus.error_tamano', 'El archivo pesa más de {mb} MB.', {
            mb: EricCorpusDocumentos.MAX_MB,
          }),
        },
      });
      return;
    }

    this.setState({ subiendoArchivo: archivo.name, errorSubida: null, exitoSubida: null });
    this._service
      .subir(archivo)
      .then(function (doc) {
        if (!self._isMounted) return;
        self.setState({
          subiendoArchivo: null,
          exitoSubida: { titulo: doc.titulo, aviso: doc.aviso },
        });
        self._cargarDocumentos();
      })
      .catch(function (error) {
        if (!self._isMounted) return;
        self.setState({
          subiendoArchivo: null,
          errorSubida: {
            archivo: archivo.name,
            motivo: error.mensaje || 'No pudimos cargar el documento.',
          },
        });
      });
  }

  // --- Pausar / reactivar -------------------------------------------------

  _cambiarEstado(doc) {
    var self = this;
    var nuevo = doc.estado === 'activo' ? 'inactivo' : 'activo';
    this.setState({ cambiandoEstadoId: doc.id, errorSubida: null, exitoSubida: null });
    this._service
      .cambiarEstado(doc.id, nuevo)
      .then(function (res) {
        if (!self._isMounted) return;
        // Actualizar la fila y el medidor en el lugar: el backend ya devolvió el
        // presupuesto recalculado, no hace falta volver a listar.
        self.setState(function (s) {
          return {
            cambiandoEstadoId: null,
            presupuestoEnUso: res.presupuestoEnUso,
            documentos: s.documentos.map(function (d) {
              return d.id === doc.id ? Object.assign({}, d, { estado: res.estado }) : d;
            }),
          };
        });
      })
      .catch(function () {
        if (!self._isMounted) return;
        self.setState({
          cambiandoEstadoId: null,
          errorSubida: {
            archivo: doc.titulo,
            motivo: self.props.intl.formatMessage({
              id: 'eric.corpus.error_estado',
              defaultMessage: 'No pudimos cambiar el estado. Probá de nuevo en unos segundos.',
            }),
          },
        });
      });
  }

  // --- Helpers ------------------------------------------------------------

  /** Traduce tokens a páginas aproximadas (mínimo 1). */
  _paginas(tokens) {
    var caracteres = tokens * 4;
    return Math.max(1, Math.round(caracteres / EricCorpusDocumentos.CARACTERES_POR_PAGINA));
  }

  _t(id, defaultMessage, values) {
    return this.props.intl.formatMessage({ id: id, defaultMessage: defaultMessage }, values);
  }

  // --- Piezas de UI -------------------------------------------------------

  renderInputArchivo() {
    return (
      <input
        type="file"
        ref={this._fileInputRef}
        className="ec-input-oculto"
        accept=".pdf,.docx,.md"
        onChange={this.handleArchivoSeleccionado}
        tabIndex={-1}
      />
    );
  }

  renderAvisos() {
    var error = this.state.errorSubida;
    var exito = this.state.exitoSubida;
    if (!error && !exito) return null;

    return (
      <div className={'ec-aviso ' + (error ? 'ec-aviso-error' : 'ec-aviso-ok')} role="status">
        <i
          className={'fa ' + (error ? 'fa-exclamation-circle' : 'fa-check-circle') + ' ec-aviso-icono'}
          aria-hidden="true"
        />
        <div className="ec-aviso-cuerpo">
          {error ? (
            <React.Fragment>
              <b>
                <FormattedMessage
                  id="eric.corpus.rechazo_titulo"
                  defaultMessage="No pudimos leer «{archivo}»"
                  values={{ archivo: error.archivo }}
                />
              </b>
              <p>{error.motivo}</p>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <b>
                <FormattedMessage
                  id="eric.corpus.exito_titulo"
                  defaultMessage="«{titulo}» ya está cargado — ERIC lo usa desde ahora"
                  values={{ titulo: exito.titulo }}
                />
              </b>
              {exito.aviso ? <p>{exito.aviso}</p> : null}
            </React.Fragment>
          )}
        </div>
        <button
          type="button"
          className="ec-aviso-cerrar"
          onClick={this.handleCerrarAviso}
          aria-label={this._t('eric.corpus.cerrar_aviso', 'Cerrar aviso')}
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      </div>
    );
  }

  renderMedidor() {
    var total = this.state.presupuestoTotal;
    var usado = this.state.presupuestoEnUso;
    var pct = total > 0 ? Math.min(100, Math.round((usado / total) * 100)) : 0;
    var casiLleno = pct >= 75;
    var restantes = this._paginas(Math.max(0, total - usado));

    return (
      <div className={'ec-medidor' + (casiLleno ? ' ec-medidor-alerta' : '')}>
        <div className="ec-medidor-fila">
          <span className="ec-medidor-label">
            <FormattedMessage id="eric.corpus.espacio_usado" defaultMessage="Espacio usado" />
          </span>
          <span className="ec-medidor-valor">{pct}%</span>
        </div>
        <div
          className="ec-barra"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={this._t('eric.corpus.espacio_usado', 'Espacio usado')}
        >
          <div className="ec-barra-fill" style={{ width: pct + '%' }} />
        </div>
        <div className="ec-medidor-pie">
          {casiLleno ? (
            <FormattedMessage
              id="eric.corpus.espacio_poco"
              defaultMessage="Queda poco espacio: pausá o borrá algún documento."
            />
          ) : (
            <FormattedMessage
              id="eric.corpus.espacio_restante"
              defaultMessage="Entran unas {paginas} páginas más · los pausados no ocupan espacio"
              values={{ paginas: restantes }}
            />
          )}
        </div>
      </div>
    );
  }

  renderEjemplo() {
    return (
      <div className="ec-ejemplo">
        <div className="ec-ejemplo-titulo">
          <FormattedMessage
            id="eric.corpus.ejemplo_titulo"
            defaultMessage="Qué pasa cuando subís un procedimiento"
          />
        </div>
        <div className="ec-ejemplo-turno">
          <span className="ec-ejemplo-quien">
            <FormattedMessage
              id="eric.corpus.ejemplo_pregunta"
              defaultMessage="Alguien de tu equipo pregunta"
            />
          </span>
          <div className="ec-ejemplo-burbuja ec-ejemplo-usuario">
            <FormattedMessage
              id="eric.corpus.ejemplo_texto_pregunta"
              defaultMessage="¿Puedo cerrar el ticket de un choque sin la denuncia?"
            />
          </div>
        </div>
        <div className="ec-ejemplo-turno">
          <span className="ec-ejemplo-quien">
            <FormattedMessage
              id="eric.corpus.ejemplo_respuesta"
              defaultMessage="ERIC responde con tu procedimiento"
            />
          </span>
          <div className="ec-ejemplo-burbuja ec-ejemplo-eric">
            <FormattedMessage
              id="eric.corpus.ejemplo_texto_respuesta"
              defaultMessage="No. En tu empresa la orden de trabajo se abre recién con el número de denuncia cargado en la ficha del móvil."
            />
            <span className="ec-cita">
              <i className="fa fa-file-text-o" aria-hidden="true" />
              <FormattedMessage
                id="eric.corpus.ejemplo_cita"
                defaultMessage="Procedimiento de siniestros"
              />
            </span>
          </div>
        </div>
        <p className="ec-ejemplo-pie">
          <FormattedMessage
            id="eric.corpus.ejemplo_pie"
            defaultMessage="ERIC siempre dice de qué documento sacó la respuesta, así podés detectar un procedimiento desactualizado."
          />
        </p>
      </div>
    );
  }

  renderFilaDocumento(doc, puedeModificar) {
    var self = this;
    var activo = doc.estado === 'activo';
    var cambiando = this.state.cambiandoEstadoId === doc.id;

    return (
      <li className={'ec-fila' + (activo ? '' : ' ec-fila-pausada')} key={doc.id}>
        <div className="ec-fila-datos">
          <div className="ec-doc-titulo">{doc.titulo}</div>
          <div className="ec-doc-meta">
            <span>{doc.archivo_nombre}</span>
            <span className="ec-doc-sep" aria-hidden="true">
              ·
            </span>
            <FormattedMessage
              id="eric.corpus.doc_paginas"
              defaultMessage="{paginas, plural, one {1 página} other {unas # páginas}}"
              values={{ paginas: this._paginas(doc.tokens_estimados) }}
            />
          </div>
        </div>

        <span className={'ec-chip' + (activo ? ' ec-chip-on' : '')}>
          <span className="ec-punto" aria-hidden="true" />
          {activo ? (
            <FormattedMessage id="eric.corpus.eric_lo_usa" defaultMessage="ERIC lo usa" />
          ) : (
            <FormattedMessage id="eric.corpus.pausado" defaultMessage="Pausado" />
          )}
        </span>

        {puedeModificar ? (
          <button
            type="button"
            className={'ec-btn-estado' + (activo ? '' : ' ec-btn-estado-reactivar')}
            disabled={cambiando}
            onClick={function () {
              self._cambiarEstado(doc);
            }}
            title={
              activo
                ? this._t('eric.corpus.pausar_tip', 'ERIC deja de usar este documento. No se borra.')
                : this._t('eric.corpus.reactivar_tip', 'ERIC vuelve a usar este documento.')
            }
          >
            <i
              className={'fa ' + (cambiando ? 'fa-spinner fa-spin' : activo ? 'fa-pause' : 'fa-play')}
              aria-hidden="true"
            />
            {activo ? (
              <FormattedMessage id="eric.corpus.pausar" defaultMessage="Pausar" />
            ) : (
              <FormattedMessage id="eric.corpus.reactivar" defaultMessage="Reactivar" />
            )}
          </button>
        ) : null}
      </li>
    );
  }

  renderFilaSubiendo() {
    return (
      <li className="ec-fila ec-fila-subiendo" aria-live="polite">
        <div className="ec-fila-datos">
          <div className="ec-doc-titulo">{this.state.subiendoArchivo}</div>
          <div className="ec-doc-meta">
            <FormattedMessage
              id="eric.corpus.extrayendo"
              defaultMessage="Extrayendo el texto para que ERIC pueda leerlo…"
            />
          </div>
        </div>
        <span className="ec-chip ec-chip-cargando">
          <i className="fa fa-spinner fa-spin" aria-hidden="true" />
          <FormattedMessage id="eric.corpus.procesando" defaultMessage="Procesando" />
        </span>
      </li>
    );
  }

  // --- Estados de pantalla ------------------------------------------------

  renderEstadoVacio(puedeCrear) {
    var subiendo = this.state.subiendoArchivo;
    var zonaClases =
      'card-body ec-zona-hero' +
      (this.state.arrastrando ? ' ec-arrastrando' : '') +
      (subiendo ? ' ec-ocupada' : '');

    return (
      <div className="row">
        <div className="col-12 col-lg-8 offset-lg-2">
          <div className="ec-hero-texto">
            <h1 className="ec-h1">
              <FormattedMessage
                id="eric.corpus.vacio_titulo"
                defaultMessage="Enseñale a ERIC cómo trabaja tu empresa"
              />
            </h1>
            <p className="ec-prosa">
              <FormattedMessage
                id="eric.corpus.vacio_sub"
                defaultMessage="Subí tus procedimientos internos y ERIC va a responder con ellos, además de lo que ya sabe sobre la plataforma. Lo que subas acá lo usa todo tu equipo."
              />
            </p>
          </div>

          {puedeCrear ? (
            <div className="card ec-card">
              <div className="card-content">
                <div
                  className={zonaClases}
                  onClick={this.handleElegirArchivo}
                  onKeyDown={this.handleTeclaEnZona}
                  onDragOver={this.handleDragOver}
                  onDragLeave={this.handleDragLeave}
                  onDrop={this.handleDrop}
                  role="button"
                  tabIndex={subiendo ? -1 : 0}
                  aria-disabled={subiendo ? 'true' : 'false'}
                >
                  <div className="ec-zona-titulo">
                    {subiendo ? (
                      <FormattedMessage
                        id="eric.corpus.leyendo"
                        defaultMessage="Leyendo el documento…"
                      />
                    ) : (
                      <FormattedMessage
                        id="eric.corpus.arrastrar"
                        defaultMessage="Arrastrá un documento acá"
                      />
                    )}
                  </div>
                  {!subiendo ? (
                    <React.Fragment>
                      <div className="ec-zona-sub">
                        <FormattedMessage
                          id="eric.corpus.o_elegir"
                          defaultMessage="o hacé clic para elegirlo"
                        />
                      </div>
                      <div className="ec-zona-formatos">
                        <FormattedMessage
                          id="eric.corpus.formatos_capacidad"
                          defaultMessage="PDF, Word o Markdown · hasta {mb} MB cada uno · entran unas {paginas} páginas de texto en total"
                          values={{
                            mb: EricCorpusDocumentos.MAX_MB,
                            paginas: this._paginas(this.state.presupuestoTotal),
                          }}
                        />
                      </div>
                    </React.Fragment>
                  ) : null}
                  {this.renderInputArchivo()}
                </div>
              </div>
            </div>
          ) : (
            <div className="card ec-card">
              <div className="card-content">
                <div className="card-body ec-sin-permiso">
                  <FormattedMessage
                    id="eric.corpus.sin_permiso_crear"
                    defaultMessage="Todavía no hay documentos cargados. No tenés permiso para subir: pedíselo a quien administra los perfiles de tu empresa."
                  />
                </div>
              </div>
            </div>
          )}

          {this.renderAvisos()}
          {this.renderEjemplo()}
        </div>
      </div>
    );
  }

  renderConDocumentos(puedeCrear, puedeModificar) {
    var self = this;
    var docs = this.state.documentos;
    var activos = docs.filter(function (d) {
      return d.estado === 'activo';
    }).length;
    var pausados = docs.length - activos;
    var subiendo = this.state.subiendoArchivo;

    // Toda la tarjeta de la lista acepta drag & drop: soltar sobre lo que ya
    // hay cargado es lo que un usuario intenta primero.
    var propsDrop = puedeCrear
      ? {
          onDragOver: this.handleDragOver,
          onDragLeave: this.handleDragLeave,
          onDrop: this.handleDrop,
        }
      : {};

    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12">
            <div className="card ec-card">
              <div className="card-content">
                <div className="card-body ec-encabezado">
                  <div className="ec-encabezado-texto">
                    <h1 className="ec-h1">
                      <FormattedMessage
                        id="eric.corpus.titulo"
                        defaultMessage="Documentos de tu empresa"
                      />
                    </h1>
                    <p className="ec-sub">
                      <FormattedMessage
                        id="eric.corpus.resumen"
                        defaultMessage="{activos, plural, =0 {Ningún documento en uso} one {# documento en uso} other {# documentos en uso}}{pausados, plural, =0 {} one { · # pausado} other { · # pausados}}"
                        values={{ activos: activos, pausados: pausados }}
                      />
                    </p>
                  </div>
                  <div className="ec-encabezado-medidor">{this.renderMedidor()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div
              className={
                'card ec-card ec-card-lista' +
                (this.state.arrastrando ? ' ec-arrastrando' : '')
              }
              {...propsDrop}
            >
              <div className="card-header ec-lista-header">
                <h2 className="card-title ec-lista-titulo">
                  <FormattedMessage
                    id="eric.corpus.cantidad_documentos"
                    defaultMessage="{n, plural, one {# documento} other {# documentos}}"
                    values={{ n: docs.length }}
                  />
                </h2>
                {puedeCrear ? (
                  <div className="ec-lista-acciones">
                    <span className="ec-lista-hint">
                      <FormattedMessage
                        id="eric.corpus.hint_arrastrar"
                        defaultMessage="o arrastrá un archivo sobre la lista"
                      />
                    </span>
                    <button
                      type="button"
                      className="ec-btn-primario"
                      onClick={this.handleElegirArchivo}
                      disabled={!!subiendo}
                    >
                      <i
                        className={'fa ' + (subiendo ? 'fa-spinner fa-spin' : 'fa-plus')}
                        aria-hidden="true"
                      />
                      {subiendo ? (
                        <FormattedMessage id="eric.corpus.leyendo" defaultMessage="Leyendo el documento…" />
                      ) : (
                        <FormattedMessage id="eric.corpus.btn_subir" defaultMessage="Subir documento" />
                      )}
                    </button>
                    {this.renderInputArchivo()}
                  </div>
                ) : null}
              </div>

              <div className="card-content">
                {this.renderAvisos()}
                <ul className="ec-lista">
                  {subiendo ? this.renderFilaSubiendo() : null}
                  {docs.map(function (doc) {
                    return self.renderFilaDocumento(doc, puedeModificar);
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  render() {
    if (this.state.redirectTo) {
      return <Redirect to={this.state.redirectTo} />;
    }

    var puedeCrear = Security.hasPermission('ERIC_CORPUS_CREAR');
    var puedeModificar = Security.hasPermission('ERIC_CORPUS_MODIFICAR');
    var hayDocumentos = this.state.documentos.length > 0;

    return (
      <div className="content-wrapper eric-corpus">
        <style>{`
          /* Tokens del producto vivo (docs/design-system/nuevo-desarrollo-frontend.md §3).
             No hay archivo de tokens en la app: los hex se declaran acá, una sola vez. */
          .eric-corpus { font-family: Poppins, sans-serif; color: #241b3e; font-size: 14px; line-height: 1.5; }

          .eric-corpus .ec-miga { color: #6B6F82; font-size: 12px; margin: 4px 0 18px; }
          .eric-corpus .ec-miga a { color: #6B6F82; transition: color 150ms; }
          .eric-corpus .ec-miga a:hover { color: #079391; }
          .eric-corpus .ec-miga b { color: #241b3e; font-weight: 600; }

          .eric-corpus .ec-card { border: 1px solid #e3ebf3; border-radius: 10px;
            box-shadow: 0 2px 4px rgba(26,16,54,.08); margin-bottom: 20px; }
          .eric-corpus .ec-card .card-content { position: relative; }

          .eric-corpus .ec-h1 { font-size: 24px; font-weight: 600; letter-spacing: -.01em;
            margin: 0; color: #241b3e; line-height: 1.25; }
          .eric-corpus .ec-sub { color: #6B6F82; margin: 5px 0 0; font-size: 13.5px; }
          /* La única restricción de ancho de la pantalla: texto corrido, 62ch. */
          .eric-corpus .ec-prosa { color: #6B6F82; margin: 8px 0 0; font-size: 15px; max-width: 62ch; }
          .eric-corpus .ec-input-oculto { display: none; }

          /* ---------- estado vacío ---------- */
          .eric-corpus .ec-hero-texto { margin: 6px 0 22px; }
          .eric-corpus .ec-hero-texto .ec-h1 { font-size: 28px; }

          .eric-corpus .ec-zona-hero { text-align: center; padding: 52px 28px; cursor: pointer;
            border: 1.5px dashed #c9d3df; border-radius: 10px; margin: 0; background: #fbfcfe;
            transition: border-color 200ms, background-color 200ms; }
          .eric-corpus .ec-zona-hero:hover { border-color: #00c0bd; background: #f4fdfd; }
          .eric-corpus .ec-zona-hero:focus-visible { outline: 2px solid #00c0bd; outline-offset: 3px; }
          .eric-corpus .ec-zona-hero.ec-arrastrando { border-color: #00c0bd; background: #e2f7f6; }
          .eric-corpus .ec-zona-hero.ec-ocupada { cursor: default; opacity: .65; }
          .eric-corpus .ec-zona-titulo { font-size: 17px; font-weight: 600; color: #241b3e; }
          .eric-corpus .ec-zona-sub { color: #6B6F82; font-size: 14px; margin-top: 4px; }
          .eric-corpus .ec-zona-formatos { color: #6B6F82; font-size: 12.5px; margin-top: 16px; }

          .eric-corpus .ec-sin-permiso { color: #6B6F82; font-size: 14px; }

          /* ---------- avisos ---------- */
          .eric-corpus .ec-aviso { display: flex; align-items: flex-start; gap: 12px;
            margin: 16px 0 0; padding: 13px 16px; border-radius: 8px; border: 1px solid; }
          .eric-corpus .ec-card-lista .ec-aviso { margin: 16px 20px 0; }
          .eric-corpus .ec-aviso-error { background: #fff2f4; border-color: #ffb3bd; }
          .eric-corpus .ec-aviso-ok { background: #e8f9f8; border-color: #7fdedc; }
          .eric-corpus .ec-aviso-icono { flex: none; font-size: 17px; line-height: 1.35; }
          .eric-corpus .ec-aviso-error .ec-aviso-icono { color: #d81e37; }
          .eric-corpus .ec-aviso-ok .ec-aviso-icono { color: #079391; }
          .eric-corpus .ec-aviso-cuerpo { flex: 1; min-width: 0; }
          .eric-corpus .ec-aviso-cuerpo b { display: block; font-size: 14px; color: #241b3e; }
          .eric-corpus .ec-aviso-cuerpo p { margin: 3px 0 0; color: #6B6F82; font-size: 13.5px; }
          .eric-corpus .ec-aviso-cerrar { flex: none; width: 40px; height: 40px; margin: -8px -8px -8px 0;
            background: none; border: 0; border-radius: 8px; color: #6B6F82; cursor: pointer;
            transition: background-color 150ms, color 150ms; }
          .eric-corpus .ec-aviso-cerrar:hover { background: rgba(26,16,54,.06); color: #241b3e; }
          .eric-corpus .ec-aviso-cerrar:focus-visible { outline: 2px solid #00c0bd; outline-offset: 1px; }

          /* ---------- ejemplo (onboarding del estado vacío) ---------- */
          .eric-corpus .ec-ejemplo { margin-top: 30px; padding-top: 26px; border-top: 1px solid #e3ebf3; }
          .eric-corpus .ec-ejemplo-titulo { font-size: 13.5px; font-weight: 600; color: #241b3e; margin-bottom: 16px; }
          .eric-corpus .ec-ejemplo-turno { margin-bottom: 14px; }
          .eric-corpus .ec-ejemplo-quien { display: block; font-size: 12.5px; color: #6B6F82; margin-bottom: 5px; }
          .eric-corpus .ec-ejemplo-burbuja { display: inline-block; padding: 10px 14px;
            border-radius: 12px; font-size: 14px; max-width: 62ch; }
          .eric-corpus .ec-ejemplo-usuario { background: #241b3e; color: #fff; border-bottom-left-radius: 4px; }
          .eric-corpus .ec-ejemplo-eric { background: #F4F5FA; border: 1px solid #e3ebf3; border-bottom-left-radius: 4px; }
          .eric-corpus .ec-cita { display: inline-flex; align-items: center; gap: 6px; margin-top: 9px;
            font-size: 12px; font-weight: 600; color: #079391; background: #e2f7f6;
            border-radius: 4px; padding: 3px 9px; }
          .eric-corpus .ec-ejemplo-pie { color: #6B6F82; font-size: 13px; margin: 6px 0 0; max-width: 62ch; }

          /* ---------- gestión: encabezado ---------- */
          .eric-corpus .ec-encabezado { display: flex; align-items: center;
            justify-content: space-between; gap: 40px; }
          .eric-corpus .ec-encabezado-texto { flex: 1; min-width: 0; }
          .eric-corpus .ec-encabezado-medidor { flex: 0 1 380px; min-width: 260px; }

          .eric-corpus .ec-medidor-fila { display: flex; align-items: baseline;
            justify-content: space-between; gap: 8px; margin-bottom: 6px; }
          .eric-corpus .ec-medidor-label { font-size: 12.5px; color: #6B6F82; }
          .eric-corpus .ec-medidor-valor { font-size: 15px; font-weight: 700; color: #241b3e;
            font-variant-numeric: tabular-nums; }
          .eric-corpus .ec-barra { height: 8px; border-radius: 4px; background: #eceef5; overflow: hidden; }
          .eric-corpus .ec-barra-fill { height: 100%; background: #00c0bd; border-radius: 4px;
            transition: width 250ms ease; }
          .eric-corpus .ec-medidor-alerta .ec-barra-fill { background: #f9b31b; }
          .eric-corpus .ec-medidor-alerta .ec-medidor-valor { color: #8a5d05; }
          .eric-corpus .ec-medidor-pie { font-size: 12.5px; color: #6B6F82; margin-top: 7px; line-height: 1.4; }
          .eric-corpus .ec-medidor-alerta .ec-medidor-pie { color: #8a5d05; }

          /* ---------- gestión: lista ---------- */
          .eric-corpus .ec-card-lista { transition: border-color 200ms, box-shadow 200ms; }
          .eric-corpus .ec-card-lista.ec-arrastrando { border-color: #00c0bd;
            box-shadow: 0 0 0 3px rgba(0,192,189,.18), 0 2px 4px rgba(26,16,54,.08); }

          .eric-corpus .ec-lista-header { display: flex; align-items: center; justify-content: space-between;
            gap: 16px; padding: 14px 20px; border-bottom: 1px solid #e3ebf3; }
          .eric-corpus .ec-lista-titulo { font-size: 15px; font-weight: 600; margin: 0; color: #241b3e; }
          .eric-corpus .ec-lista-acciones { display: flex; align-items: center; gap: 14px; }
          .eric-corpus .ec-lista-hint { color: #6B6F82; font-size: 12.5px; }

          .eric-corpus .ec-btn-primario { display: inline-flex; align-items: center; gap: 8px;
            min-height: 40px; padding: 0 18px; border: 0; border-radius: 8px;
            background: #00c0bd; color: #0b2a2c; font-family: inherit; font-size: 13.5px;
            font-weight: 600; cursor: pointer;
            transition: background-color 150ms, transform 150ms; }
          .eric-corpus .ec-btn-primario:hover { background: #0abfbc; }
          .eric-corpus .ec-btn-primario:active { transform: scale(.97); }
          .eric-corpus .ec-btn-primario:focus-visible { outline: 2px solid #241b3e; outline-offset: 2px; }
          .eric-corpus .ec-btn-primario:disabled { opacity: .6; cursor: default; transform: none; }

          .eric-corpus .ec-lista { list-style: none; margin: 0; padding: 0; }
          .eric-corpus .ec-fila { display: flex; align-items: center; gap: 18px;
            padding: 14px 20px; border-bottom: 1px solid #e3ebf3; min-height: 68px; }
          .eric-corpus .ec-fila:last-child { border-bottom: 0; }
          .eric-corpus .ec-fila-datos { flex: 1; min-width: 0; }
          .eric-corpus .ec-doc-titulo { font-weight: 600; font-size: 15px; color: #241b3e; overflow-wrap: anywhere; }
          .eric-corpus .ec-doc-meta { color: #6B6F82; font-size: 12.5px; margin-top: 3px; overflow-wrap: anywhere; }
          .eric-corpus .ec-doc-sep { margin: 0 6px; }
          .eric-corpus .ec-fila-subiendo { background: #fbfcfe; }
          /* pausado: sigue ahí pero se lee apagado, sin ocultarlo */
          .eric-corpus .ec-fila-pausada .ec-doc-titulo { color: #6B6F82; }

          .eric-corpus .ec-chip { flex: none; display: inline-flex; align-items: center; justify-content: center;
            gap: 7px; min-width: 118px; min-height: 30px; padding: 0 12px;
            font-size: 12.5px; font-weight: 600; border-radius: 999px;
            border: 1px solid #d7dfe8; color: #6B6F82; background: #F4F5FA; white-space: nowrap; }
          .eric-corpus .ec-chip-on { color: #07716f; background: #e2f7f6; border-color: #7fdedc; }
          .eric-corpus .ec-punto { width: 7px; height: 7px; border-radius: 50%; background: #9aa3b2; }
          .eric-corpus .ec-chip-on .ec-punto { background: #00c0bd; }
          .eric-corpus .ec-chip-cargando { color: #241b3e; background: #fff; }

          /* botón de estado: secundario, no compite con el chip. Target ≥ 40px. */
          .eric-corpus .ec-btn-estado { flex: none; display: inline-flex; align-items: center; justify-content: center;
            gap: 8px; min-width: 112px; min-height: 40px; padding: 0 14px;
            background: #fff; border: 1px solid #d7dfe8; border-radius: 8px;
            font-family: inherit; font-size: 13.5px; font-weight: 600; color: #241b3e; cursor: pointer;
            transition: border-color 150ms, background-color 150ms, color 150ms, transform 150ms; }
          .eric-corpus .ec-btn-estado:hover { border-color: #241b3e; background: #F4F5FA; }
          .eric-corpus .ec-btn-estado:active { transform: scale(.97); }
          .eric-corpus .ec-btn-estado:focus-visible { outline: 2px solid #00c0bd; outline-offset: 2px; }
          .eric-corpus .ec-btn-estado:disabled { opacity: .6; cursor: default; transform: none; }
          .eric-corpus .ec-btn-estado-reactivar { color: #07716f; border-color: #7fdedc; }
          .eric-corpus .ec-btn-estado-reactivar:hover { background: #e2f7f6; border-color: #00c0bd; }

          .eric-corpus .ec-cargando { padding: 48px; text-align: center; }

          @media (prefers-reduced-motion: reduce) {
            .eric-corpus * { transition: none !important; }
            .eric-corpus .fa-spin { animation-duration: 2.4s; }
          }

          @media (max-width: 991px) {
            .eric-corpus .ec-encabezado { flex-direction: column; align-items: stretch; gap: 18px; }
            .eric-corpus .ec-encabezado-medidor { flex: none; min-width: 0; }
            .eric-corpus .ec-lista-hint { display: none; }
          }
          @media (max-width: 767px) {
            .eric-corpus .ec-lista-header { flex-wrap: wrap; }
            .eric-corpus .ec-fila { flex-wrap: wrap; gap: 10px; }
            .eric-corpus .ec-fila-datos { flex-basis: 100%; }
            .eric-corpus .ec-hero-texto .ec-h1 { font-size: 24px; }
            .eric-corpus .ec-zona-hero { padding: 36px 18px; }
          }
        `}</style>

        <div className="ec-miga">
          <Link to="/eric/chat">
            <FormattedMessage id="eric.corpus.miga_eric" defaultMessage="ERIC" />
          </Link>
          {' · '}
          <b>
            <FormattedMessage
              id="eric.corpus.miga_documentos"
              defaultMessage="Documentos de tu empresa"
            />
          </b>
        </div>

        {this.state.cargando ? (
          <div className="ec-cargando">
            <Loading />
          </div>
        ) : hayDocumentos || this.state.subiendoArchivo ? (
          this.renderConDocumentos(puedeCrear, puedeModificar)
        ) : (
          this.renderEstadoVacio(puedeCrear)
        )}
      </div>
    );
  }
}

export default injectIntl(EricCorpusDocumentos);
